// Typed client for the per-project mode-document store (the locked A <-> B
// interface). One JSON document per (project, kind); each editor mode owns its
// document's data shape and documents it in the mode file, so this client is
// generic over the payload type.
import { apiGet, apiPut } from './api';

export const MODE_DOC_KINDS = [
  'logic',
  'hud',
  'audio',
  'fx',
  'gamedata',
  'pixel',
  'model',
  'gamesetup',
  'prefabs',
  'cutscenes',
  'introsequences',
] as const;

export type ModeDocKind = (typeof MODE_DOC_KINDS)[number];

export interface ModeDocument<T extends Record<string, unknown> = Record<string, unknown>> {
  kind: ModeDocKind;
  data: T;
  /** ISO timestamp of the last save; null when the document has never been saved. */
  updated_at: string | null;
}

/**
 * Load a mode document. A document that has never been saved resolves with
 * empty data and a null updated_at rather than rejecting.
 */
export function loadModeDocument<T extends Record<string, unknown> = Record<string, unknown>>(
  projectId: string,
  kind: ModeDocKind,
): Promise<ModeDocument<T>> {
  return apiGet<ModeDocument<T>>(`/projects/${projectId}/docs/${kind}`);
}

/**
 * Save a mode document, replacing its data wholesale. keepalive lets a save
 * fired during page teardown (tab close, navigation) outlive the document.
 */
export function saveModeDocument<T extends Record<string, unknown> = Record<string, unknown>>(
  projectId: string,
  kind: ModeDocKind,
  data: T,
  opts?: { keepalive?: boolean },
): Promise<ModeDocument<T>> {
  return apiPut<ModeDocument<T>>(`/projects/${projectId}/docs/${kind}`, { data }, opts);
}
